import { useDocumentAutofillContext } from '../components/documentAutofill/DocumentAutofillContext'
import type { BoxTruckProfileDocumentEntity } from '../services/boxTruckDirectDocumentUpload'
import { readProfileDocument, readSavedProfileDocument } from '../services/documentAutofillApi'
import { pendingSourceId, storedSourceId } from '../utils/documentAutofill/sourceIds'
import type { AutofillSource } from './useDocumentAutofill'

interface Options {
    /** The kind of profile the card's saved documents are on, so the server can find them. */
    entity: BoxTruckProfileDocumentEntity
    /** The saved profile's id; none on a profile not created yet, whose files are all pending. */
    entityId?: string
}

interface StoredDocument {
    documentId: string
    fileName: string
    removalIncomplete?: boolean
}

/** What the card's AI button does now: `fill` reads the card, `unavailable` says why it can't (shown on hover). */
export type CardFill = { fill: () => void; unavailable?: undefined } | { fill?: undefined; unavailable: string }

/**
 * A document card's side of AI autofill. Adding a file never reads it: HR presses the card's AI button, which reads every file on it
 * not read yet (a pending file uploaded with the request, a stored document read by the server from storage). Removing a file takes back what only it filled. What was
 * read lives in the form's autofill, above the card, so a card that remounts still knows its files.
 */
export function useProfileDocumentAutofill<T extends string, D extends StoredDocument>({ entity, entityId }: Options) {
    const autofill = useDocumentAutofillContext()

    const sourcesOf = (stored: readonly D[], pending: readonly File[]): AutofillSource[] => [
        ...(entityId
            ? stored
                  // A document whose removal did not finish is on its way out: it is not read.
                  .filter((document) => !document.removalIncomplete)
                  .map(
                      (document): AutofillSource => ({
                          id: storedSourceId(document.documentId),
                          fileName: document.fileName,
                          // Read by the server from storage: the browser never downloads it to send it back.
                          read: (hint, key, signal) => readSavedProfileDocument({ entity, entityId, documentId: document.documentId }, hint, key, signal),
                      })
                  )
            : []),
        ...pending.map(
            (file): AutofillSource => ({
                id: pendingSourceId(file),
                fileName: file.name,
                read: (hint, key, signal) => readProfileDocument(file, hint, key, signal),
            })
        ),
    ]

    /** The card's AI button, or undefined on a card the form does not read. */
    const cardFill = (type: T, stored: readonly D[], pending: readonly File[]): CardFill | undefined => {
        if (!autofill?.labelOf(type)) return undefined
        const status = autofill.statusOf(type)
        if (status?.status === 'reading') return { unavailable: 'Reading…' }
        const sources = sourcesOf(stored, pending)
        if (sources.length === 0) return { unavailable: 'Add a file first.' }
        const read = new Set(status?.readIds ?? [])
        if (sources.every((source) => read.has(source.id))) return { unavailable: 'All files read. Add a new file to read again.' }
        return { fill: () => autofill.start(type, sources) }
    }

    /** The card's file with this id: a stored document or a pending file. */
    const fileOf = (sourceId: string, stored: readonly D[], pending: readonly File[]): { stored: D } | { pending: File } | undefined => {
        const document = stored.find((candidate) => storedSourceId(candidate.documentId) === sourceId)
        if (document) return { stored: document }
        const file = pending.find((candidate) => pendingSourceId(candidate) === sourceId)
        return file ? { pending: file } : undefined
    }

    /** HR removed a file from the card: what only it filled goes, and a read of it stops. */
    const forgetFile = (type: T, removed: File | D) =>
        autofill?.forget(type, removed instanceof File ? pendingSourceId(removed) : storedSourceId(removed.documentId))

    return { cardFill, fileOf, forgetFile }
}
